import { Component } from '@theme/component';
import { sectionRenderer } from '@theme/section-renderer';
import { ThemeEvents } from '@theme/events';

class CustomPagination extends Component {
  #filterUpdateHandler = () => {
    const section = document.querySelector('results-list[section-id]');
    if (!section) return;

    const observer = new MutationObserver((mutations) => {
      for (const m of mutations) {
        if (m.type !== 'childList') continue;
        const isGridTarget = m.target instanceof HTMLElement && m.target.classList.contains('product-grid');
        const hasGridNode = [...m.addedNodes, ...m.removedNodes].some(
          (n) => n instanceof HTMLElement && n.classList.contains('product-grid')
        );
        if (!isGridTarget && !hasGridNode) continue;
        observer.disconnect();
        this.#initialize();
        return;
      }
    });

    observer.observe(section, { childList: true, subtree: true });
    setTimeout(() => observer.disconnect(), 5000);
  };

  connectedCallback() {
    super.connectedCallback();

    // Clear the navigation flag if it was set for this navigation
    // (The redirect check is handled by the immediate function at the end of the file)
    const navFlag = sessionStorage.getItem('custom-pagination-navigation');
    const hasNavigationFlag = navFlag && navFlag.startsWith('true:');
    if (hasNavigationFlag) {
      sessionStorage.removeItem('custom-pagination-navigation');
    }

    this.#initialize();

    // Listen for per_page changes to reinitialize
    window.addEventListener('per-page-changed', () => {
      setTimeout(() => {
        this.#initialize();
      }, 100);
    });

    // Listen for products-loaded event (triggered after search filter)
    window.addEventListener('products-loaded', () => {
      setTimeout(() => {
        this.#initialize();
      }, 100);
    });

    // Listen for facet filter changes (e.g. availability filter)
    window.addEventListener(ThemeEvents.FilterUpdate, this.#filterUpdateHandler);

    // When sectionRenderer morphs this element in place, it clears our rendered buttons
    // without re-firing connectedCallback. Detect that and re-initialize.
    this.#selfObserver = new MutationObserver(() => {
      if (this.#rendering) return;
      this.#initialize();
    });
    this.#selfObserver.observe(this, { childList: true });
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.#selfObserver?.disconnect();
    this.#selfObserver = null;
    window.removeEventListener(ThemeEvents.FilterUpdate, this.#filterUpdateHandler);
  }

  #initialize() {
    // Get current displayPerPage from URL or data attribute
    const urlParams = new URLSearchParams(window.location.search);
    const urlPerPage = urlParams.get('per_page');
    const displayPerPage = urlPerPage ? parseInt(urlPerPage, 10) : parseInt(this.dataset.displayPerPage || '12', 10);

    if (displayPerPage !== 12 && displayPerPage !== 24 && displayPerPage !== 48) {
      return;
    }

    // Get total products from the grid's data attribute (updated by section renderer)
    // Find grid by looking for any product-grid with data-display-per-page attribute
    const productGridElement = document.querySelector('.product-grid[data-display-per-page]');
    if (!(productGridElement instanceof HTMLElement)) {
      return;
    }

    // Update grid's display-per-page to match current URL value
    if (displayPerPage !== parseInt(productGridElement.dataset.displayPerPage || '12', 10)) {
      productGridElement.dataset.displayPerPage = displayPerPage.toString();
    }

    // Check if there's a search query - if so, count products from DOM
    const searchQuery = urlParams.get('q');

    let totalProducts;
    if (searchQuery) {
      // When searching, count actual products in DOM
      const actualProducts = productGridElement.querySelectorAll('[ref="cards[]"]');
      totalProducts = actualProducts.length;
      // Update grid's data attributes to reflect filtered count
      productGridElement.dataset.totalProducts = totalProducts.toString();
      productGridElement.dataset.paginateItems = totalProducts.toString();
    } else {
      // Get total products from grid - prefer paginate.items if available, otherwise use totalProducts
      const paginateItems = parseInt(productGridElement.dataset.paginateItems || '0', 10);
      const totalProductsFromGrid = parseInt(productGridElement.dataset.totalProducts || '0', 10);
      // Use paginate.items if available (more accurate), otherwise use totalProducts
      totalProducts = paginateItems > 0 ? paginateItems : (totalProductsFromGrid || parseInt(this.dataset.totalProducts || '0', 10));
    }

    const productsPerLoad = parseInt(this.dataset.productsPerLoad || '48', 10);

    this.#displayPerPage = displayPerPage;
    this.#productsPerLoad = productsPerLoad;
    this.#totalDisplayPages = Math.ceil(totalProducts / displayPerPage);

    // Hide pagination if products count is less than or equal to displayPerPage
    if (totalProducts <= displayPerPage) {
      this.style.display = 'none';
      return;
    } else {
      this.style.display = '';
    }

    this.#productGrid = productGridElement;

    const urlPage = parseInt(urlParams.get('page') || '1', 10);
    // If searching and we have filtered products, reset to page 1 if current page is invalid
    if (searchQuery && totalProducts > 0) {
      const maxPage = Math.ceil(totalProducts / displayPerPage);
      this.#currentPage = urlPage > maxPage ? 1 : urlPage;
      // Update URL if we reset to page 1
      if (this.#currentPage === 1 && urlPage > maxPage) {
        const url = new URL(window.location.href);
        url.searchParams.set('page', '1');
        window.history.replaceState({}, '', url.toString());
      }
    } else {
      this.#currentPage = urlPage;
    }

    const startIndex = (this.#currentPage - 1) * this.#displayPerPage;
    this.#loadedPage = Math.floor(startIndex / this.#productsPerLoad) + 1;

    const currentLoadedPage = parseInt(productGridElement.dataset.currentPage || '1', 10);
    if (this.#loadedPage !== currentLoadedPage) {
      this.#loadedPage = currentLoadedPage;
    }

    // Update data attributes
    this.dataset.displayPerPage = displayPerPage.toString();
    this.dataset.totalProducts = totalProducts.toString();
    this.dataset.currentPage = this.#currentPage.toString();
    this.dataset.loadedPage = this.#loadedPage.toString();

    this.#renderPagination();

    requestAnimationFrame(() => {
      this.#updateProductVisibility();
    });
  }

  #displayPerPage = 12;
  #currentPage = 1;
  #loadedPage = 1;
  #productsPerLoad = 48;
  #totalDisplayPages = 1;
  /** @type {HTMLElement | null} */
  #productGrid = null;
  #rendering = false;
  /** @type {MutationObserver | null} */
  #selfObserver = null;

  #renderPagination() {
    const totalPages = this.#totalDisplayPages;
    const currentPage = this.#currentPage;

    let startPage = Math.max(1, currentPage - 2);
    let endPage = Math.min(totalPages, startPage + 4);

    if (endPage - startPage < 4) {
      startPage = Math.max(1, endPage - 4);
    }

    let paginationHTML = '<div class="custom-pagination">';
    const prevDisabled = currentPage === 1 ? 'disabled' : '';
    paginationHTML += '<button class="custom-pagination__button custom-pagination__button--prev" ' + prevDisabled + ' data-page="' + (currentPage - 1) + '"><span class="svg-wrapper icon-arrow"><svg width="19" height="16" viewBox="0 0 19 16" xmlns="http://www.w3.org/2000/svg" focusable="false" aria-hidden="true"><path d="M18 8H2M2 8L8 2M2 8L8 14" stroke="currentColor" stroke-width="2" fill="none"/></svg></span></button>';

    for (let i = startPage; i <= endPage; i++) {
      const active = i === currentPage ? 'active' : '';
      paginationHTML += '<button class="custom-pagination__button custom-pagination__button--number ' + active + '" data-page="' + i + '">' + i + '</button>';
    }

    const nextDisabled = currentPage === totalPages ? 'disabled' : '';
    paginationHTML += '<button class="custom-pagination__button custom-pagination__button--next" ' + nextDisabled + ' data-page="' + (currentPage + 1) + '"><span class="svg-wrapper icon-arrow"><svg width="19" height="16" viewBox="0 0 19 16" xmlns="http://www.w3.org/2000/svg" focusable="false" aria-hidden="true"><path d="M1 8H17M17 8L11 2M17 8L11 14" stroke="currentColor" fill="none" stroke-width="2"/></svg></span></button>';
    paginationHTML += '</div>';

    this.#rendering = true;
    this.innerHTML = paginationHTML;

    this.querySelectorAll('.custom-pagination__button').forEach(button => {
      if (!(button instanceof HTMLElement)) return;
      button.addEventListener('click', (e) => {
        e.preventDefault();
        const page = parseInt(button.dataset.page || '1', 10);
        if (page >= 1 && page <= totalPages && !button.hasAttribute('disabled')) {
          this.#goToPage(page);
        }
      });
    });

    // Use a macrotask so MutationObserver callbacks (microtasks) see the flag as true,
    // then clear it so future external clears (morphdom) are detected.
    setTimeout(() => { this.#rendering = false; }, 0);
  }

  async #updateProductVisibility() {
    if (!this.#productGrid) return;

    const currentPage = this.#currentPage;

    // Check if we're in search mode (all products are in DOM)
    const urlParams = new URLSearchParams(window.location.search);
    const searchQuery = urlParams.get('q');

    if (!searchQuery) {
      // Normal pagination mode - may need to load additional pages
      const startIndex = (currentPage - 1) * this.#displayPerPage;
      const neededLoadPage = Math.floor(startIndex / this.#productsPerLoad) + 1;

      if (neededLoadPage !== this.#loadedPage) {
        await this.#loadPage(neededLoadPage);
      }
    }
    // When searching, all products are already in DOM, just show/hide them

    this.#showProductsForPage(currentPage);
  }

  /**
   * @param {number} page
   */
  #showProductsForPage(page) {
    if (!this.#productGrid) return;

    const startIndex = (page - 1) * this.#displayPerPage;
    const endIndex = startIndex + this.#displayPerPage;
    const productItems = Array.from(this.#productGrid.querySelectorAll('[ref="cards[]"]'));

    // Check if we're in search mode (all products are in DOM)
    const urlParams = new URLSearchParams(window.location.search);
    const searchQuery = urlParams.get('q');

    productItems.forEach((item, index) => {
      if (item instanceof HTMLElement) {
        let itemIndex;
        if (searchQuery) {
          // When searching, all filtered products are in DOM, use array index directly
          itemIndex = index;
        } else {
          // Calculate global index: use data-product-index from current page, or calculate from loadedPage
          const productIndex = parseInt(item.dataset.productIndex || '0', 10);
          // Global index = (loadedPage - 1) * productsPerLoad + productIndex
          itemIndex = (this.#loadedPage - 1) * this.#productsPerLoad + productIndex;
        }

        if (itemIndex >= startIndex && itemIndex < endIndex) {
          item.style.display = '';
        } else {
          item.style.display = 'none';
        }
      }
    });
  }

  /**
   * @param {number} pageNumber
   */
  async #loadPage(pageNumber) {
    const productGrid = this.#productGrid;
    if (!productGrid) return;

    const url = new URL(window.location.href);
    url.searchParams.set('page', pageNumber.toString());
    url.hash = '';

    // Preserve search query if present
    const currentUrlParams = new URLSearchParams(window.location.search);
    const searchQuery = currentUrlParams.get('q');
    if (searchQuery) {
      url.searchParams.set('q', searchQuery);
    }

    try {
      const resultsList = productGrid.closest('results-list');
      const sectionId = resultsList?.getAttribute('section-id');

      if (!sectionId) {
        throw new Error('Section ID not found');
      }

      const pageHTML = await sectionRenderer.getSectionHTML(sectionId, true, url);

      if (!pageHTML) {
        throw new Error('Failed to load page');
      }

      const parser = new DOMParser();
      const doc = parser.parseFromString(pageHTML, 'text/html');
      const newGrid = doc.querySelector('.product-grid[ref="grid"]');

      if (!newGrid) {
        throw new Error('Product grid not found in loaded page');
      }

      const newProductItems = Array.from(newGrid.querySelectorAll('[ref="cards[]"]'));

      const oldItems = Array.from(productGrid.querySelectorAll('[ref="cards[]"]'));
      oldItems.forEach(item => item.remove());

      newProductItems.forEach(item => {
        if (item instanceof HTMLElement) {
          productGrid.appendChild(item);
        }
      });

      this.#loadedPage = pageNumber;
    } catch (error) {
      console.error('Error loading page:', error);
    }
  }

  /**
   * @param {number} page
   */
  async #goToPage(page) {
    if (page < 1 || page > this.#totalDisplayPages) {
      return;
    }

    this.#currentPage = page;

    // Set flag with timestamp to indicate this is programmatic navigation (not a page refresh)
    // Format: 'true:timestamp' to track when navigation happened
    const timestamp = Date.now();
    sessionStorage.setItem('custom-pagination-navigation', `true:${timestamp}`);

    const url = new URL(window.location.href);
    url.searchParams.set('page', page.toString());
    url.hash = '';
    window.history.pushState({}, '', url.toString());

    await this.#updateProductVisibility();

    this.#renderPagination();

    const productGrid = this.#productGrid;
    if (productGrid) {
      productGrid.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }
  }
}

// Immediate check on script load - before component initialization
(function () {
  const urlParams = new URLSearchParams(window.location.search);
  const urlPage = parseInt(urlParams.get('page') || '1', 10);
  const navFlag = sessionStorage.getItem('custom-pagination-navigation');
  const hasNavigationFlag = navFlag && navFlag.startsWith('true:');

  let isValidFlag = false;
  if (hasNavigationFlag) {
    try {
      const timestamp = parseInt(navFlag.split(':')[1] || '0', 10);
      const now = Date.now();
      isValidFlag = (now - timestamp) < 1000;
    } catch (e) {
      isValidFlag = false;
    }
  }

  // Redirect if page > 1 and no valid flag exists (works for both regular pages and search results)
  // All URL parameters (including 'q') are automatically preserved
  if (urlPage > 1 && !isValidFlag) {
    sessionStorage.removeItem('custom-pagination-navigation');
    const url = new URL(window.location.href);
    url.searchParams.set('page', '1');
    window.location.replace(url.toString());
  }
})();

if (!customElements.get('custom-pagination')) {
  customElements.define('custom-pagination', CustomPagination);
}
